import { useId, useMemo, useState } from 'react';
import { ChevronDown, Pencil } from 'lucide-react';
import { useAddressBookEntryByAddress } from '@/hooks/organization/use-address-book-entry-by-address';
import { AddressBookSelectorDialog } from '@/components/shared/address-book-selector-dialog';
import { FloatingInput } from '@/components/ui/floating-input';
import { TransactionBuilderParameterInputProps } from './types';
import { getParameterStateForAddress } from './utils';
import { getReadableSolidityName } from '@/lib/solidity-formatting';
import { ImportedAddressBookNotice } from '../ImportedAddressBookNotice';

export function AddressInput({
  value,
  name,
  onChange,
  paramAbiFragment,
  autoFocus,
  hideAddressLabel,
  allowUnsavedAddress = false,
  addressBookEntryByAddress,
  addressBookLookupPending = false,
  addressBookLookupFailed = false,
}: TransactionBuilderParameterInputProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const importedAddressNoticeId = useId();
  const label = getReadableSolidityName(
    paramAbiFragment.name || name,
    'Parameter'
  );
  const currentState = useMemo(
    () => getParameterStateForAddress(value),
    [value]
  );
  const selectedAddress =
    currentState.isValid && typeof currentState.parsedValue === 'string'
      ? currentState.parsedValue
      : null;
  const shouldUseBatchLookup =
    allowUnsavedAddress && addressBookEntryByAddress !== undefined;
  const {
    entry: individuallySelectedEntry,
    isLoading: isLoadingSelectedEntry,
    error: selectedEntryError,
  } = useAddressBookEntryByAddress(
    shouldUseBatchLookup ? null : selectedAddress
  );
  const selectedEntry = selectedAddress
    ? (addressBookEntryByAddress?.get(selectedAddress.toLowerCase()) ??
      individuallySelectedEntry)
    : null;
  // Batched imports must finish their lookup before an absent label is treated
  // as an unsaved address. A failed lookup cannot establish that either.
  const isAddressBookLookupPending = shouldUseBatchLookup
    ? addressBookLookupPending
    : isLoadingSelectedEntry;
  const hasAddressBookLookupError = shouldUseBatchLookup
    ? addressBookLookupFailed
    : Boolean(selectedEntryError);
  const hasUnsavedImportedAddress = Boolean(
    allowUnsavedAddress &&
      selectedAddress &&
      !selectedEntry &&
      !isAddressBookLookupPending &&
      !hasAddressBookLookupError
  );
  const selectedLabel = selectedEntry?.name?.trim() || null;
  const hasAddressBookSelection = Boolean(selectedAddress && selectedEntry);
  const hasMissingAddressBookEntry =
    value !== '' &&
    currentState.isValid &&
    selectedAddress !== null &&
    !selectedEntry &&
    !isLoadingSelectedEntry &&
    !allowUnsavedAddress;
  const hasInvalidValue =
    value !== '' && (!currentState.isValid || hasMissingAddressBookEntry);
  const displayValue = hasAddressBookSelection
    ? selectedLabel || selectedAddress || ''
    : allowUnsavedAddress && selectedAddress
      ? selectedAddress
      : !currentState.isValid
        ? value
        : '';
  const helperText =
    hasAddressBookSelection && selectedLabel && selectedAddress
      ? selectedAddress
      : null;
  const showMonospaceValue =
    hasInvalidValue ||
    Boolean(hasAddressBookSelection && !selectedLabel) ||
    Boolean(allowUnsavedAddress && selectedAddress && !selectedLabel);

  const openAddressBook = () => {
    setOpen(true);
  };

  const handleAddressSelect = (address: string) => {
    onChange?.(name, getParameterStateForAddress(address));
  };

  const handleAddressClear = () => {
    onChange?.(name, getParameterStateForAddress(''));
  };

  return (
    <>
      <div className="flex flex-col gap-1 w-full">
        <FloatingInput
          value={displayValue}
          aria-label={label}
          aria-describedby={
            hasUnsavedImportedAddress ? importedAddressNoticeId : undefined
          }
          onChange={() => {
            // no-op: address parameters are selected via the dialog
          }}
          readOnly
          autoFocus={autoFocus}
          placeholder="Select address"
          className={[
            'cursor-pointer',
            showMonospaceValue ? 'font-mono' : null,
            hasInvalidValue
              ? 'border-red-500 text-red-500 focus-visible:border-red-500'
              : null,
          ]
            .filter(Boolean)
            .join(' ')}
          onMouseDown={(event) => {
            event.preventDefault();
            openAddressBook();
          }}
          onKeyDown={(event) => {
            if (
              event.key === 'Enter' ||
              event.key === ' ' ||
              event.key === 'Spacebar' ||
              event.key === 'ArrowDown'
            ) {
              event.preventDefault();
              openAddressBook();
            }
          }}
          aria-haspopup="dialog"
          aria-expanded={open}
          endAdornment={
            hasAddressBookSelection ? (
              <span
                className="flex h-4 w-4 cursor-pointer items-center justify-center text-zinc-500"
                onMouseDown={(event) => {
                  event.preventDefault();
                  openAddressBook();
                }}
              >
                <Pencil className="h-4 w-4" />
              </span>
            ) : (
              <span
                className="flex h-4 w-4 cursor-pointer items-center justify-center text-zinc-500"
                onMouseDown={(event) => {
                  event.preventDefault();
                  openAddressBook();
                }}
              >
                <ChevronDown className="h-4 w-4" />
              </span>
            )
          }
          {...(!hideAddressLabel ? { label } : {})}
        />

        {helperText ? (
          <p className="font-mono text-xs text-muted-foreground px-1 break-all">
            {helperText}
          </p>
        ) : null}

        {hasUnsavedImportedAddress && selectedAddress ? (
          <ImportedAddressBookNotice
            address={selectedAddress}
            label={label}
            id={importedAddressNoticeId}
          />
        ) : null}

        {hasInvalidValue ? (
          <p className="text-xs text-red-500 px-1">
            Select a valid address from the address book.
          </p>
        ) : null}
      </div>

      <AddressBookSelectorDialog
        open={open}
        onOpenChange={setOpen}
        mode="single"
        title={`Select ${label}`}
        description={
          allowUnsavedAddress
            ? 'Choose another address from the address book. Changing this value will use the edited transaction details.'
            : 'Choose an address from the address book, or save a new one before using it for this parameter.'
        }
        selectedAddress={
          allowUnsavedAddress
            ? selectedAddress
            : hasAddressBookSelection
              ? selectedAddress
              : null
        }
        onAddressSelect={(address) => handleAddressSelect(address)}
        onAddressClear={handleAddressClear}
        searchPlaceholder="Search address book or paste address"
        singleSelectionRequiresConfirm
      />
    </>
  );
}
